import {
  accountStatuses,
  addDays,
  cashSafeToSpend,
  incomeToSavings,
  moneyOnHand,
  owedToYouMinor,
  receivableStatuses,
  savingsSummary,
  splitBySpending,
  usedForSpending,
  type ReceivableLike,
  type ReceivableStatus,
  type SavingsSummary,
  type AccountLike,
  type AccountStatus,
  type CashSafeToSpend,
  type ExpectedMoneyLike,
  computeCashFlow,
  cutoffContaining,
  fundStatus,
  monthEnd,
  monthStart,
  nextPayday,
  duePaydays,
  paydaysInRange,
  pickCutoffSource,
  reservedMinor,
  safeToSpend,
  type CashFlow,
  type CommitmentItem,
  type CommitmentLine,
  type DateRange,
  type EffectivePayday,
  type FundEntryLike,
  type FundLike,
  type FundStatus,
  type IncomeSourceLike,
  type LocalDate,
  type PaydayRowLike,
  type PlannedLine,
  type SafeToSpend,
  type WeekStart,
} from '@tahi/core';
import type { AccountEntry, BudgetSettingsView, ReceiptEntry } from '../../data/repos/budget';

export type { BudgetSettingsView };

/** Everything Budget's numbers are worked out from, for one window of days. Pure data, no database. */
export interface BudgetSnapshot {
  userId: string;
  today: LocalDate;
  weekStart: WeekStart;
  /** Income sources in the order they were added, including deleted ones (their received paydays keep a name). */
  sources: IncomeSourceLike[];
  rows: PaydayRowLike[];
  funds: FundLike[];
  settings: BudgetSettingsView;
  /** Ledger rows dated inside `window`. */
  entries: (FundEntryLike & { accountId?: string | null })[];
  /** From the commitments providers for `window`; empty until Payments ships. */
  commitments: CommitmentLine[];
  /** v0.5: live money accounts, and every ledger row that moves one (balances need them all). */
  accounts: AccountLike[];
  accountEntries: AccountEntry[];
  /** v0.5: expected money ("Coming in"), never income. */
  expected: ExpectedMoneyLike[];
  /** v0.6: money owed to you, and Budget's deposit rows (their receipts among them). */
  receivables: ReceivableLike[];
  receipts: ReceiptEntry[];
  /** From the planned-spending providers (Shopping list); empty until it ships. Never subtracted. */
  planned: PlannedLine[];
  window: DateRange;
}

/** The window loaded for "today" numbers: wide enough for any fund period or cutoff around today. */
export function todayWindow(today: LocalDate): DateRange {
  return { start: addDays(today, -62), end: addDays(today, 62) };
}

/** The window loaded for a month's cash flow, including cutoffs that run into the next month. */
export function monthWindow(month: string): DateRange {
  const start = `${month}-01`;
  return { start, end: addDays(monthEnd(start), 45) };
}

export const liveSources = (s: BudgetSnapshot) => s.sources.filter((x) => x.deletedAt === null);

export type FlowPeriod = 'cutoff' | 'month';

/** Paydays a few weeks either side, so an early or late one is never counted as other income. */
const PAYDAY_MARGIN_DAYS = 62;

/**
 * The cash flow of a cutoff or a month. In a cutoff a received payday counts in the cutoff of its
 * scheduled day (an early salary stays in its own cutoff); a month counts it on the day it came.
 */
export function cashFlowOf(s: BudgetSnapshot, range: DateRange, by: FlowPeriod): CashFlow {
  const paydays = paydaysInRange(s.userId, s.sources, s.rows, {
    start: addDays(range.start, -PAYDAY_MARGIN_DAYS),
    end: addDays(range.end, PAYDAY_MARGIN_DAYS),
  });
  return computeCashFlow({
    range,
    today: s.today,
    paydays,
    paydaysBy: by === 'cutoff' ? 'scheduled' : 'date',
    entries: s.entries,
    commitments: s.commitments,
    planned: s.planned,
    expected: s.expected,
    toSavingsIds: toSavingsIdsOf(s, paydays),
  });
}

/** v0.6: which of these paydays (and one-off income rows) land in a savings-only account. */
export function toSavingsIdsOf(
  s: BudgetSnapshot,
  paydays: readonly EffectivePayday[],
): Set<string> {
  return incomeToSavings({
    paydays,
    // Every ledger row in the window: a received payday whose row has no account must be found.
    entries: s.entries,
    sources: s.sources,
    settings: s.settings,
    accounts: s.accounts,
  });
}

/** The current cutoff or calendar month. */
export function currentRange(s: BudgetSnapshot, period: FlowPeriod): DateRange {
  if (period === 'month') return { start: monthStart(s.today), end: monthEnd(s.today) };
  return cutoffContaining(s.today, pickCutoffSource(s.sources));
}

export function fundStatusesOf(s: BudgetSnapshot): FundStatus[] {
  return s.funds
    .filter((f) => f.deletedAt === null)
    .map((f) => fundStatus(f, s.entries, s.today, { weekStart: s.weekStart, sources: s.sources }));
}

/** v0.5: are spending limits (funds) in use? Off by default; fund rows are kept. */
export const fundsOn = (s: BudgetSnapshot): boolean => s.settings.useFunds;

/** v0.5: every live account with its balance today ("Your money"). */
export function accountStatusesOf(s: BudgetSnapshot): AccountStatus[] {
  return accountStatuses(s.accounts, s.accountEntries, s.today);
}

/** v0.6: the accounts used for spending ("Your money"; savings-only ones are under Savings). */
export function spendingStatusesOf(s: BudgetSnapshot): AccountStatus[] {
  return splitBySpending(accountStatusesOf(s)).spending;
}

/** "Your money": only accounts used for spending (savings-only ones never count in Safe to spend). */
export function moneyOnHandOf(s: BudgetSnapshot): number {
  return moneyOnHand(spendingStatusesOf(s));
}

/** v0.6 "Savings": savings-only accounts, their total and estimated interest so far. */
export function savingsOf(s: BudgetSnapshot): SavingsSummary {
  return savingsSummary(accountStatusesOf(s), s.accountEntries, s.today);
}

/** v0.6 "Owed to you": open ones first, with what's left to receive. */
export function receivablesOf(s: BudgetSnapshot): ReceivableStatus[] {
  return receivableStatuses(s.receivables, s.receipts);
}

export function owedToYouOf(s: BudgetSnapshot): number {
  return owedToYouMinor(receivablesOf(s));
}

export type SafeToSpendView = SafeToSpend | CashSafeToSpend;

/**
 * "Safe to spend" (v0.5, Ivan): from real money when accounts are set up (money on hand +
 * unconfirmed salary − every payment due before the next payday). With spending limits on and a
 * default limit: that limit's left minus payments reserved. Otherwise the cutoff's cash flow.
 */
export function safeToSpendOf(s: BudgetSnapshot): SafeToSpendView {
  // Real money first: with accounts it is always cash-based, spending limits on or off (a weekly
  // limit minus every payment due gave a number that meant nothing).
  // v0.6: only accounts used for spending count; with none (savings only), the older ways apply.
  // Money owed to you never counts until it is received.
  if (s.accounts.some(usedForSpending)) {
    const due = duePaydays(s.userId, s.sources, s.rows, s.today);
    const toSavings = toSavingsIdsOf(s, due);
    return cashSafeToSpend({
      onHandMinor: moneyOnHandOf(s),
      // A payday that goes straight to a savings-only account isn't spendable money.
      duePaydays: due.filter((p) => !toSavings.has(p.id)),
      commitments: s.commitments,
      today: s.today,
      nextPayday: nextPayday(s.userId, s.sources, s.rows, s.today)?.date ?? null,
    });
  }
  const fund = fundsOn(s) ? defaultFundOf(s) : null;
  if (fund) {
    const st = fundStatus(fund, s.entries, s.today, { weekStart: s.weekStart, sources: s.sources });
    return safeToSpend({
      basis: 'fund',
      leftMinor: st.leftMinor,
      range: st.range,
      commitments: s.commitments,
      today: s.today,
    });
  }
  return safeToSpend({
    basis: 'cashflow',
    cashFlow: cashFlowOf(s, currentRange(s, 'cutoff'), 'cutoff'),
  });
}

/** Unpaid payments due from today to the day before the next payday (overdue ones dated today). */
export function dueBeforePaydayOf(s: BudgetSnapshot): CommitmentItem[] {
  const next = nextPayday(s.userId, s.sources, s.rows, s.today)?.date ?? addDays(s.today, 31);
  return cashSafeToSpend({
    onHandMinor: 0,
    duePaydays: [],
    commitments: s.commitments,
    today: s.today,
    nextPayday: next,
  }).due;
}

/** The default fund if it is still live. */
export function defaultFundOf(s: BudgetSnapshot): FundLike | null {
  return s.funds.find((f) => f.id === s.settings.defaultFundId && f.deletedAt === null) ?? null;
}

/** Per-tile setting for "What's left" (spec §6: which fund, per cutoff or per month). */
export interface WhatsLeftConfig {
  /** 'default' (the default fund, else all income), 'income', or a fund id. */
  source?: string;
  period?: FlowPeriod;
}

export interface WhatsLeftView {
  basis: 'fund' | 'cashflow';
  /** The fund shown; null for all income. */
  fundId: string | null;
  /** The fund's name; null for all income. */
  fundName: string | null;
  leftMinor: number;
  /** Fund amount, or the income the period is worked out with. */
  totalMinor: number;
  spentMinor: number;
  reservedMinor: number;
  /** Planned spending (Shopping list items with estimates), shown beside the number, never subtracted. */
  plannedMinor: number;
  range: DateRange;
  nextStart: LocalDate;
}

/** Total planned spending right now (spec §6 Shopping list: "₱850 on your shopping list"). */
export function plannedMinorOf(s: BudgetSnapshot): number {
  return s.planned.flatMap((l) => l.items).reduce((sum, i) => sum + i.amountMinor, 0);
}

export function whatsLeftOf(s: BudgetSnapshot, config: WhatsLeftConfig = {}): WhatsLeftView {
  const source = config.source ?? 'default';
  const live = s.funds.filter((f) => f.deletedAt === null);
  const fund =
    source === 'income' || !fundsOn(s)
      ? null
      : source === 'default'
        ? defaultFundOf(s)
        : (live.find((f) => f.id === source) ?? defaultFundOf(s));
  if (fund) {
    const st = fundStatus(fund, s.entries, s.today, { weekStart: s.weekStart, sources: s.sources });
    return {
      basis: 'fund',
      fundId: fund.id,
      fundName: fund.name,
      leftMinor: st.leftMinor,
      totalMinor: fund.amountMinor,
      spentMinor: st.spentMinor,
      reservedMinor: reservedMinor(s.commitments, st.range, s.today),
      plannedMinor: plannedMinorOf(s),
      range: st.range,
      nextStart: addDays(st.range.end, 1),
    };
  }
  const period = config.period ?? 'cutoff';
  const range = currentRange(s, period);
  const cf = cashFlowOf(s, range, period);
  return {
    basis: 'cashflow',
    fundId: null,
    fundName: null,
    leftMinor: cf.whatsLeftMinor,
    totalMinor: cf.incomeMinor,
    spentMinor: cf.otherSpendingMinor,
    reservedMinor: reservedMinor(s.commitments, range, s.today),
    plannedMinor: plannedMinorOf(s),
    range,
    nextStart: addDays(range.end, 1),
  };
}

export function duePaydaysOf(s: BudgetSnapshot): EffectivePayday[] {
  return duePaydays(s.userId, s.sources, s.rows, s.today);
}

export function nextPaydayOf(s: BudgetSnapshot): EffectivePayday | null {
  return nextPayday(s.userId, s.sources, s.rows, s.today);
}
